import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Siren, WarningOctagon, Plus } from "@phosphor-icons/react";

const SEVERITIES = ["Informational","Voluntary Withdrawal","Low Risk","Moderate Risk","High Risk","Emergency"];

const NewRecallDialog = ({ products, batches, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    recall_title: "",
    product_id: "",
    batch_ids: [],
    severity: "Voluntary Withdrawal",
    reason: "",
    internal_description: "",
    public_description: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/recalls", form);
      toast.success("Recall recorded. Batches quarantined.");
      onCreated();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const productBatches = batches.filter((b) => b.product_id === form.product_id);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-recall-btn" className="inline-flex items-center gap-2 bg-red-500 text-white hover:bg-red-400 font-semibold px-4 py-2 text-xs tracking-wide">
          <Plus size={14} weight="bold" /> Initiate recall
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white border-brand-border text-brand-navy max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Initiate recall</DialogTitle>
          <DialogDescription className="text-xs text-brand-muted font-mono">
            Recalled batches are immediately quarantined. Customer notifications are NOT sent until an approver runs “Approve notice”.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Recall title</Label>
            <Input required value={form.recall_title} onChange={(e) => setForm({ ...form, recall_title: e.target.value })} data-testid="recall-title" />
          </div>
          <div>
            <Label>Product</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v, batch_ids: [] })}>
              <SelectTrigger data-testid="recall-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.product_name} — {p.strength}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.product_id && (
            <div>
              <Label>Affected batches</Label>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {productBatches.length === 0 ? (
                  <div className="text-xs text-brand-muted font-mono">No batches recorded for this product.</div>
                ) : productBatches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-xs text-brand-text">
                    <input
                      type="checkbox"
                      checked={form.batch_ids.includes(b.id)}
                      onChange={(e) => setForm({
                        ...form,
                        batch_ids: e.target.checked
                          ? [...form.batch_ids, b.id]
                          : form.batch_ids.filter((x) => x !== b.id),
                      })}
                    />
                    <span className="font-mono">Lot {b.public_lot_number} — {b.status}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason (internal)</Label>
            <Textarea required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          <div>
            <Label>Public description (shown to affected customers)</Label>
            <Textarea value={form.public_description} onChange={(e) => setForm({ ...form, public_description: e.target.value })} />
          </div>
          <button type="submit" disabled={busy} data-testid="recall-submit" className="w-full bg-red-500 text-white hover:bg-red-400 font-semibold px-4 py-2 text-sm disabled:opacity-50">
            {busy ? "Recording…" : "Record recall"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminRecalls() {
  const [recalls, setRecalls] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);

  const load = async () => {
    const [r, p, b] = await Promise.all([
      api.get("/admin/recalls"),
      api.get("/admin/products"),
      api.get("/admin/batches"),
    ]);
    setRecalls(r.data);
    setProducts(p.data);
    setBatches(b.data);
  };

  useEffect(() => { load(); }, []);

  const approveNotice = async (id) => {
    if (!window.confirm("Send recall notices to affected verified accounts?")) return;
    try {
      await api.post(`/admin/recalls/${id}/approve-notice`);
      toast.success("Notice approved and queued");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const productName = (pid) => products.find((p) => p.id === pid)?.product_name || pid.slice(0, 6);

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-recalls-page">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="overline mb-2">Safety</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy flex items-center gap-3">
              <Siren size={30} weight="duotone" className="text-red-400" /> Product Recalls
            </h1>
            <p className="mt-2 text-brand-muted text-sm max-w-2xl">
              Recording a recall immediately quarantines affected batches. Customer notifications only send after an approver signs off.
            </p>
          </div>
          <NewRecallDialog products={products} batches={batches} onCreated={load} />
        </div>

        {recalls.length === 0 ? (
          <div className="border border-brand-border bg-white p-16 text-center" data-testid="recalls-empty">
            <div className="font-display text-xl text-brand-navy">No recalls on record.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {recalls.map((r) => (
              <div key={r.id} className="border border-brand-border bg-white p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-display text-brand-navy text-lg">{r.recall_title}</div>
                    <div className="text-xs text-brand-muted font-mono">
                      {productName(r.product_id)} · Severity <span className="text-red-300">{r.severity}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border border-brand-border text-brand-muted px-2 py-0.5">
                      Notice: {r.notification_status}
                    </span>
                    {r.notification_status === "not_started" && (
                      <button
                        onClick={() => approveNotice(r.id)}
                        data-testid={`recall-approve-${r.id.slice(0,6)}`}
                        className="inline-flex items-center gap-1 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-3 py-1.5 text-xs"
                      >
                        Approve notice
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-sm text-brand-text">{r.reason}</p>
                {r.public_description && <p className="mt-2 text-xs text-brand-muted">Public: {r.public_description}</p>}
                <div className="mt-3 text-[10px] font-mono text-brand-muted">Batches affected: {r.batch_ids.length}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminGuard>
  );
}
